import { useState, useCallback } from 'react';
import { Stack, Group, Text, Card, Badge, Button, TextInput, Accordion } from '../../../../components/ui';
import { IconTrash, IconKey, IconSearch } from '@tabler/icons-react';
import { alert, toast } from '../../../../components/ui/providers/AlertProvider';
import type { CacheManagementTabProps } from '../types';

export function CacheManagementTab({
  serviceGroups,
  domainStats,
  onRefresh,
  onDeleteByPattern,
}: CacheManagementTabProps) {
  const [pattern, setPattern] = useState('');

  const handleDeleteByPattern = useCallback(async () => {
    if (!pattern) {
      await alert.error('Error', 'Please enter a pattern');
      return;
    }

    const confirmed = await alert.confirm(
      'Delete Cache Keys',
      `Are you sure you want to delete all keys matching pattern "${pattern}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    await toast.promise(onDeleteByPattern(pattern), {
      loading: `Deleting keys matching ${pattern}...`,
      success: `Keys matching ${pattern} deleted successfully`,
      error: 'Failed to delete keys',
    });

    setPattern('');
    onRefresh();
  }, [pattern, onDeleteByPattern, onRefresh]);

  return (
    <Stack gap="md">
      {/* Pattern Deletion */}
      <Card>
        <Stack gap="sm">
          <Text size="md" fw={600}>
            Delete by Pattern
          </Text>
          <Group>
            <TextInput
              flex={1}
              placeholder="e.g., ccms:user:* or ccms:member:*"
              leftSection={<IconSearch size={16} />}
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
            />
            <Button leftSection={<IconTrash size={16} />} color="red" onClick={handleDeleteByPattern}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Service Groups */}
      <Card>
        <Stack gap="sm">
          <Text size="md" fw={600}>
            Keys by Service
          </Text>
          <Accordion>
            {Object.entries(serviceGroups).map(([service, group]) => (
              <Accordion.Item key={service} value={service}>
                <Accordion.Control>
                  <Group justify="space-between">
                    <Group gap="sm">
                      <IconKey size={16} />
                      <Text fw={500}>{group.serviceName}</Text>
                    </Group>
                    <Badge>{group.keyCount} keys</Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    {group.sampleKeys.map((key) => (
                      <Text key={key} size="xs" c="dimmed" family="monospace">
                        {key}
                      </Text>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Stack>
      </Card>

      {/* Domain Statistics */}
      <Card>
        <Stack gap="sm">
          <Text size="md" fw={600}>
            Domain Statistics
          </Text>
          {Object.entries(domainStats).map(([domain, stats]) => (
            <Group key={domain} justify="space-between">
              <Text size="sm">{stats.domain}</Text>
              <Group gap="md">
                <Badge variant="light">{stats.keyCount} keys</Badge>
                <Text size="xs" c="dimmed">
                  ~{(stats.estimatedMemoryBytes / 1024).toFixed(1)} KB
                </Text>
              </Group>
            </Group>
          ))}
        </Stack>
      </Card>
    </Stack>
  );
}
